/* eslint-disable arrow-body-style */
import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { AnalyticsFacade } from '@amfam/shared/analytics/lib/+state/analytics.facade';
import { ProfileAnalytics } from '@amfam/shared/models/lib/profile/profile-constants';

import { DeleteService } from '../../services/delete.service';
import {
  DeleteMyAccountAction,
  DeleteMyAccountActionFail,
  DeleteMyAccountActionSuccess,
  SendTemplatedEmailsAction,
  SendTemplatedEmailsActionFail,
  SendTemplatedEmailsActionSuccess,
  UnenrollProgramsAction,
  UnenrollUserActionFail,
  UnenrollUserActionSuccess
} from './delete-myaccount.action';
import { UnenrollProgramsResponse } from './delete-myaccount.model';

// Helper function
function isUnenrollmentSuccessful(response: UnenrollProgramsResponse): boolean {
  // when messages is empty unenrollment was successful
  if (response.status.messages.length === 0) {
    return true;
  } else {
    // check if all the messages have response as 20000 then unenrollment was successful
    return response.status.messages.every(message => message.code === 20000);
  }
}

export const unenrollPrograms = createEffect(
  (
    actions$ = inject(Actions),
    deleteService = inject(DeleteService),
    analyticsFacade = inject(AnalyticsFacade)
  ) => {
    return actions$.pipe(
      ofType(UnenrollProgramsAction),
      map(action => action),
      switchMap(() => {
        return deleteService.unenrollPrograms().pipe(
          map(response => {
            if (!isUnenrollmentSuccessful(response)) {
              const error = { status: { code: response.status.messages[0]?.status } };
              analyticsFacade.trackEvent(ProfileAnalytics.deleteMyAccountFailure);
              return UnenrollUserActionFail(error);
            }
            return UnenrollUserActionSuccess(response);
          }),
          catchError(error => {
            analyticsFacade.trackEvent(ProfileAnalytics.deleteMyAccountFailure);
            return of(UnenrollUserActionFail(error));
          })
        );
      })
    );
  },
  { functional: true }
);

export const deleteMyaccount = createEffect(
  (
    actions$ = inject(Actions),
    deleteService = inject(DeleteService),
    analyticsFacade = inject(AnalyticsFacade)
  ) => {
    return actions$.pipe(
      ofType(DeleteMyAccountAction),
      map(action => action),
      switchMap(() => {
        return deleteService.deleteMyaccount().pipe(
          map(response => {
            return DeleteMyAccountActionSuccess(response);
          }),
          catchError(error => {
            analyticsFacade.trackEvent(ProfileAnalytics.deleteMyAccountFailure);
            return of(DeleteMyAccountActionFail(error));
          })
        );
      })
    );
  },
  { functional: true }
);

export const sendTemplatedEmails = createEffect(
  (
    actions$ = inject(Actions),
    deleteService = inject(DeleteService),
    analyticsFacade = inject(AnalyticsFacade)
  ) => {
    return actions$.pipe(
      ofType(SendTemplatedEmailsAction),
      map(action => action.payload),
      switchMap(payload => {
        return deleteService.sendTemplatedEmails(payload).pipe(
          map(response => {
            return SendTemplatedEmailsActionSuccess(response);
          }),
          catchError(error => {
            analyticsFacade.trackEvent(ProfileAnalytics.deleteMyAccountFailure);
            return of(SendTemplatedEmailsActionFail(error));
          })
        );
      })
    );
  },
  { functional: true }
);
